import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkline } from './Sparkline';

// ─────────────────────────────────────────────────────────────
// KPI Card — Operations Deck style
//   ┌────────────────────────────┐
//   │ ▌ LABEL (mono uppercase)   │  ← optional left accent bar
//   │   42        units      ▲+2 │  ← Fraunces 40px / mono unit / delta
//   │   subtitle                 │
//   │ ╱╲╱╲╱╲╱╲ (sparkline)       │
//   │  30D AGO          NOW      │
//   └────────────────────────────┘
// ─────────────────────────────────────────────────────────────

type Trend = 'up' | 'down' | 'flat';

interface KpiCardProps {
  label: string;
  value: number | string;
  unit?: string;
  delta?: string;
  trend?: Trend;
  sub?: string;
  data?: number[];
  accent?: string;
  icon?: ReactNode;
  href?: string;
  loading?: boolean;
}

export function KpiCard({
  label,
  value,
  unit,
  delta,
  trend,
  sub,
  data,
  accent = 'var(--color-accent)',
  icon,
  href,
  loading = false,
}: KpiCardProps) {
  const navigate = useNavigate();
  const interactive = !!href;

  const trendArrow = trend === 'up' ? '▲' : trend === 'down' ? '▼' : '·';
  const trendBg =
    trend === 'up'
      ? 'rgba(26,143,90,0.09)'
      : trend === 'down'
      ? 'rgba(180,69,59,0.09)'
      : 'var(--color-panel-alt)';
  const trendColor =
    trend === 'up'
      ? 'var(--color-good)'
      : trend === 'down'
      ? 'var(--color-bad)'
      : 'var(--color-accent)';

  const content = (
    <>
      {/* Left accent bar (subtle visual anchor) */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          top: 14,
          bottom: 14,
          width: 3,
          borderRadius: 2,
          background: accent,
          opacity: 0.85,
        }}
      />

      {/* Header row — label + delta (or icon) */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              color: 'var(--color-text-dim)',
              fontWeight: 500,
            }}
          >
            {label}
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 8 }}>
            {loading ? (
              <span
                className="animate-pulse"
                style={{
                  display: 'inline-block',
                  width: 80,
                  height: 38,
                  borderRadius: 4,
                  background: 'var(--color-border)',
                }}
              />
            ) : (
              <span
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 40,
                  fontWeight: 500,
                  letterSpacing: -1.2,
                  color: 'var(--color-text)',
                  lineHeight: 1,
                }}
              >
                {value}
              </span>
            )}
            {unit && (
              <span
                style={{
                  fontSize: 11.5,
                  color: 'var(--color-text-dim)',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {unit}
              </span>
            )}
          </div>

          {sub && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--color-text-mid)',
                marginTop: 7,
                fontFamily: 'var(--font-mono)',
              }}
            >
              {sub}
            </div>
          )}
        </div>

        {/* Top-right — delta badge OR icon */}
        {delta ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              padding: '3px 7px',
              borderRadius: 3,
              background: trendBg,
              color: trendColor,
              fontSize: 10.5,
              fontWeight: 600,
              fontFamily: 'var(--font-mono)',
              letterSpacing: 0.4,
              flexShrink: 0,
            }}
          >
            <span>{trendArrow}</span>
            <span>{delta}</span>
          </div>
        ) : icon ? (
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: 'var(--color-panel-alt)',
              color: accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </div>
        ) : null}
      </div>

      {/* Sparkline */}
      {data && data.length >= 2 && (
        <>
          <div style={{ marginTop: 12 }}>
            <Sparkline data={data} color={accent} height={44} />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 9.5,
              fontFamily: 'var(--font-mono)',
              color: 'var(--color-text-dim)',
              letterSpacing: 0.6,
              marginTop: -2,
            }}
          >
            <span>30D AGO</span>
            <span>NOW</span>
          </div>
        </>
      )}
    </>
  );

  const baseStyle = {
    background: 'var(--color-panel)',
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    padding: '18px 20px 14px 22px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
    position: 'relative' as const,
    overflow: 'hidden' as const,
    transition: 'box-shadow 180ms, transform 180ms',
    textAlign: 'left' as const,
    width: '100%',
  };

  if (interactive) {
    return (
      <button
        onClick={() => navigate(href!)}
        style={{ ...baseStyle, cursor: 'pointer', border: '1px solid var(--color-border)' }}
        className="hover:shadow-lg hover:-translate-y-0.5"
      >
        {content}
      </button>
    );
  }

  return <div style={baseStyle}>{content}</div>;
}
